// src/utils/album/pdfGenerator.js
import { jsPDF } from 'jspdf';
import { loadAllImagesAsBase64 } from './imageLoader';

// Configurações específicas para cada formato
const layoutConfigs = {
    'A4-portrait': {
        logo: {
            width: 25,
            height: 12.5,
            x: 165, // pageWidth(148) - width(25) - margin(15)
            y: 265, // pageHeight(210) - height(12.5) - margin(12.5)
        },
        cover: {
            border: { margin: 15, lineWidth: 2 },
            title: { fontSize: 32, lineHeight: 12 },
            dedication: { fontSize: 14, lineHeight: 6 },
            date: { fontSize: 10, y: 270 }  // Ajustado para o rodapé
        },
        instructions: {
            title: { fontSize: 22, y: 30 },
            text: { fontSize: 13, lineHeight: 10, marginX: 0.15 },
            lineY: 35
        },
        image: {
            title: { fontSize: 16, y: 25 },
            marginTop: 30,
            marginBottom: 35,
            marginSides: 12,
            pageNumber: { fontSize: 10, y: 270 }  // Ajustado para o rodapé (era 185)
        },
        certificate: {
            title: { fontSize: 24, y: 45 },
            lineY: 50,
            contentStartY: 0.35,
            text: { fontSize: 14 },
            nameField: { width: 0.5, y: 20 },
            dateField: { width: 0.3, y: 40 }
        }
    },
    'A4-landscape': {
        logo: {
            width: 30,     // Proporcional ao tamanho da página
            height: 15,    // Proporção mantida (30 * 0.5)
            x: 240,        // pageWidth(297) - width(30) - margin(15)
            y: 175,        // pageHeight(210) - height(15) - margin(10)
        },
        cover: {
            border: { margin: 15, lineWidth: 2 },
            title: { fontSize: 34, lineHeight: 12 },
            dedication: { fontSize: 14, lineHeight: 6 },
            date: { fontSize: 10, y: 185 }  // Alinhado com o logo
        },
        instructions: {
            title: { fontSize: 24, y: 35 },
            text: { fontSize: 14, lineHeight: 10, marginX: 0.15 },
            lineY: 40
        },
        image: {
            title: { fontSize: 16, y: 25 },
            marginTop: 35,
            marginBottom: 40,   // Espaço para logo e número
            marginSides: 15,
            pageNumber: { fontSize: 10, y: 185 }
        },
        certificate: {
            title: { fontSize: 28, y: 30 },  // Movido um pouco para cima (era 50)
            lineY: 35,                       // Ajustado junto com o título
            contentStartY: 0.3,              // Diminuído para subir o conteúdo (era 0.35)
            text: { fontSize: 16 },
            nameField: { width: 0.4, y: 20 }, // Diminuído para dar mais espaço (era 25)
            dateField: { width: 0.3, y: 40 }  // Diminuído proporcionalmente (era 45)
        }
    },
    'A5-portrait': {
        logo: {
            width: 25,
            height: 12.5,
            x: 103, // pageWidth(148) - width(25) - margin(15)
            y: 180, // pageHeight(210) - height(12.5) - margin(12.5)
        },
        cover: {
            border: { margin: 15, lineWidth: 2 },
            title: { fontSize: 32, lineHeight: 12 },
            dedication: { fontSize: 14, lineHeight: 6 },
            date: { fontSize: 10, y: 185 }
        },
        instructions: {
            title: { fontSize: 22, y: 30 },
            text: { fontSize: 13, lineHeight: 10, marginX: 0.15 },
            lineY: 35
        },
        image: {
            title: { fontSize: 16, y: 25 },
            marginTop: 30,
            marginBottom: 35,
            marginSides: 12,
            pageNumber: { fontSize: 10, y: 185 }
        },
        certificate: {
            title: { fontSize: 24, y: 45 },
            lineY: 50,
            contentStartY: 0.35,
            text: { fontSize: 14 },
            nameField: { width: 0.5, y: 20 },
            dateField: { width: 0.3, y: 40 }
        }
    },
    'A5-landscape': {
        logo: {
            width: 20,
            height: 10,
            x: 175, // pageWidth(210) - width(20) - margin(15)
            y: 124, // pageHeight(148) - height(10) - margin(5)
        },
        cover: {
            border: { margin: 12, lineWidth: 1.5 },
            title: { fontSize: 28, lineHeight: 10 },
            dedication: { fontSize: 12, lineHeight: 5 },
            date: { fontSize: 9, y: 130 }
        },
        instructions: {
            title: { fontSize: 20, y: 25 },
            text: { fontSize: 12, lineHeight: 8, marginX: 0.15 },
            lineY: 30
        },
        image: {
            title: { fontSize: 14, y: 20 },
            marginTop: 25,
            marginBottom: 30,
            marginSides: 10,
            pageNumber: { fontSize: 9, y: 130 }
        },
        certificate: {
            title: { fontSize: 22, y: 35 },
            lineY: 40,
            contentStartY: 0.32,
            text: { fontSize: 13 },
            nameField: { width: 0.45, y: 18 },
            dateField: { width: 0.25, y: 35 }
        }
    },
    'A6-portrait': {
        logo: {
            width: 18,
            height: 9,
            x: 77, // pageWidth(105) - width(18) - margin(10)
            y: 133, // pageHeight(148) - height(9) - margin(6)
        },
        cover: {
            border: { margin: 10, lineWidth: 1.5 },
            title: { fontSize: 24, lineHeight: 9 },
            dedication: { fontSize: 11, lineHeight: 5 },
            date: { fontSize: 8, y: 133 }
        },
        instructions: {
            title: { fontSize: 18, y: 25 },
            text: { fontSize: 11, lineHeight: 7, marginX: 0.15 },
            lineY: 30
        },
        image: {
            title: { fontSize: 13, y: 20 },
            marginTop: 22,
            marginBottom: 25,
            marginSides: 8,
            pageNumber: { fontSize: 8, y: 133 }
        },
        certificate: {
            title: { fontSize: 20, y: 35 },
            lineY: 40,
            contentStartY: 0.32,
            text: { fontSize: 12 },
            nameField: { width: 0.5, y: 15 },
            dateField: { width: 0.3, y: 30 }
        }
    },
    'A6-landscape': {
        logo: {
            width: 15,
            height: 7.5,
            x: 123, // pageWidth(148) - width(15) - margin(10)
            y: 92, // pageHeight(105) - height(7.5) - margin(5.5)
        },
        cover: {
            border: { margin: 8, lineWidth: 1 },
            title: { fontSize: 20, lineHeight: 8 },
            dedication: { fontSize: 10, lineHeight: 4 },
            date: { fontSize: 7, y: 92 }
        },
        instructions: {
            title: { fontSize: 16, y: 20 },
            text: { fontSize: 10, lineHeight: 6, marginX: 0.12 },
            lineY: 25
        },
        image: {
            title: { fontSize: 12, y: 18 },
            marginTop: 20,
            marginBottom: 22,
            marginSides: 8,
            pageNumber: { fontSize: 7, y: 92 }
        },
        certificate: {
            title: { fontSize: 18, y: 30 },
            lineY: 34,
            contentStartY: 0.3,
            text: { fontSize: 11 },
            nameField: { width: 0.45, y: 12 },
            dateField: { width: 0.25, y: 25 }
        }
    }
};


// Função para adicionar páginas especiais de colorir fixas
// Função para adicionar páginas especiais de colorir fixas
const addSpecialColoringPages = async (pdf, pageWidth, pageHeight, orientation) => {
    try {
        // Defina as margens e dimensões aqui, fora dos blocos
        const margin = 15;
        const imgWidth = pageWidth - (margin * 2);
        const imgHeight = pageHeight - (margin * 2);

        // Página 2 - Página com foto para colorir
        const page2Image = orientation === 'portrait' ?
            '/img/Pag2PDF_Vertical_FotoColorir.png' :
            '/img/Pag2PDF_Horizontal_FotoColorir.png';

        pdf.addPage();
        addBorder(pdf, pageWidth, pageHeight);

        // Carrega e adiciona a imagem
        const response1 = await fetch(page2Image);
        if (response1.ok) {
            const blob1 = await response1.blob();
            const base64_1 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob1);
            });

            pdf.addImage(base64_1, 'PNG', margin, margin, imgWidth, imgHeight);
        }

        // Página 3 - "Este Livro é de:"
        const page3Image = orientation === 'portrait' ?
            '/img/Pag3PDF_Vertical_LivroDe.png' :
            '/img/Pag3PDF_Horizontal_LivroDe.png';

        pdf.addPage();
        addBorder(pdf, pageWidth, pageHeight);

        const response2 = await fetch(page3Image);
        if (response2.ok) {
            const blob2 = await response2.blob();
            const base64_2 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob2);
            });

            pdf.addImage(base64_2, 'PNG', margin, margin, imgWidth, imgHeight);
        }

    } catch (error) {
        console.error('Erro ao adicionar páginas especiais:', error);
    }
};

// Função para obter configuração atual
const getLayoutConfig = (paperSize, orientation) => {
    const key = `${paperSize}-${orientation}`;
    return layoutConfigs[key] || layoutConfigs['A5-portrait']; // fallback
};

// Função para adicionar moldura decorativa
const addBorder = (pdf, pageWidth, pageHeight) => {
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);
};

// Função para adicionar logo com posicionamento específico
const addLogo = async (pdf, pageWidth, pageHeight, paperSize, orientation) => {
    try {
        const config = getLayoutConfig(paperSize, orientation);
        const logoConfig = config.logo;

        const logoPath = '/img/LogoFINAL.png';
        const logoResponse = await fetch(logoPath);
        const logoBlob = await logoResponse.blob();

        const logoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
        });

        pdf.addImage(logoBase64, 'PNG', logoConfig.x, logoConfig.y, logoConfig.width, logoConfig.height);
    } catch (error) {
        console.error('Erro ao carregar logo:', error);
    }
};

// Função principal atualizada
export const generatePDF = async ({
    selectedImages,
    albumTitle,
    dedication,
    paperSize,
    orientation,
    pageLayout,
    specialPages,
    onProgress
}) => {
    if (onProgress) onProgress(10);

    const imagesWithBase64 = await loadAllImagesAsBase64(selectedImages);
    if (onProgress) onProgress(30);

    let pageWidth, pageHeight;

    switch (paperSize) {
        case 'A4':
            pageWidth = 210;
            pageHeight = 297;
            break;
        case 'A5':
            pageWidth = 148;
            pageHeight = 210;
            break;
        default:
            pageWidth = 210;
            pageHeight = 297;
    }

    if (orientation === 'landscape') {
        [pageWidth, pageHeight] = [pageHeight, pageWidth];
    }

    const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: [pageWidth, pageHeight]
    });

    // 1. Capa (página 1)
    await createCoverPage(pdf, pageWidth, pageHeight, albumTitle, dedication, paperSize, orientation);
    if (onProgress) onProgress(35);

    // 2. Páginas especiais de colorir (páginas 2 e 3)
    await addSpecialColoringPages(pdf, pageWidth, pageHeight, orientation);
    if (onProgress) onProgress(40);

    // 3. Instruções (após as páginas especiais)
    if (specialPages.includes('instructions')) {
        await createInstructionsPage(pdf, pageWidth, pageHeight, paperSize, orientation);
    }
    if (onProgress) onProgress(45);

    // 4. Páginas das imagens do usuário
    for (let i = 0; i < imagesWithBase64.length; i++) {
        await createImagePage(pdf, pageWidth, pageHeight, imagesWithBase64[i], i, pageLayout, paperSize, orientation);
        if (onProgress) {
            const progress = 45 + (35 * (i + 1) / imagesWithBase64.length);
            onProgress(Math.round(progress));
        }
    }

    // 5. Certificado
    if (specialPages.includes('certificate')) {
        await createCertificatePage(pdf, pageWidth, pageHeight, paperSize, orientation);
    }
    if (onProgress) onProgress(90);

    // 6. Contracapa
    await createBackCoverPage(pdf, pageWidth, pageHeight, paperSize, orientation);
    if (onProgress) onProgress(95);

    return pdf;
};

// Função melhorada para capa
const createCoverPage = async (pdf, pageWidth, pageHeight, albumTitle, dedication, paperSize, orientation) => {
    const config = getLayoutConfig(paperSize, orientation);
    const coverConfig = config.cover;

    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');

    // Borda decorativa
    pdf.setDrawColor(150, 150, 150);
    pdf.setLineWidth(coverConfig.border.lineWidth);
    pdf.rect(coverConfig.border.margin, coverConfig.border.margin,
        pageWidth - (coverConfig.border.margin * 2),
        pageHeight - (coverConfig.border.margin * 2));

    // Título
    pdf.setFontSize(coverConfig.title.fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(40, 40, 40);

    const titleLines = pdf.splitTextToSize(albumTitle || "Meu Livro de Colorir", pageWidth - 60);
    const titleStartY = (pageHeight - (titleLines.length * coverConfig.title.lineHeight)) / 2;

    titleLines.forEach((line, index) => {
        pdf.text(line, pageWidth / 2, titleStartY + (index * coverConfig.title.lineHeight), { align: 'center' });
    });

    // Dedicatória
    if (dedication) {
        pdf.setFontSize(coverConfig.dedication.fontSize);
        pdf.setFont('helvetica', 'italic');
        const dedicationLines = pdf.splitTextToSize(dedication, pageWidth - 80);
        const dedicationY = titleStartY + (titleLines.length * coverConfig.title.lineHeight) + 30;

        dedicationLines.forEach((line, index) => {
            pdf.text(line, pageWidth / 2, dedicationY + (index * coverConfig.dedication.lineHeight), { align: 'center' });
        });
    }

    // Data
    pdf.setFontSize(coverConfig.date.fontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const today = new Date();
    pdf.text(`Criado em ${today.toLocaleDateString('pt-BR')}`, pageWidth / 2, coverConfig.date.y, { align: 'center' });

    await addLogo(pdf, pageWidth, pageHeight, paperSize, orientation);
};

// Função melhorada para página de instruções
const createInstructionsPage = async (pdf, pageWidth, pageHeight, paperSize, orientation) => {
    const config = getLayoutConfig(paperSize, orientation);
    const instrConfig = config.instructions;

    pdf.addPage();
    addBorder(pdf, pageWidth, pageHeight);

    // Título
    pdf.setFontSize(instrConfig.title.fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(40, 40, 40);
    pdf.text('Instruções para Colorir', pageWidth / 2, instrConfig.title.y, { align: 'center' });

    // Linha decorativa
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(pageWidth * 0.3, instrConfig.lineY, pageWidth * 0.7, instrConfig.lineY);

    // Instruções
    pdf.setFontSize(instrConfig.text.fontSize);
    pdf.setFont('helvetica', 'normal');
    const instructions = [
        '• Use lápis de cor, canetinhas ou giz de cera',
        '• Comece pelas cores mais claras',
        '• Respeite as bordas dos desenhos',
        '• Divirta-se e seja criativo!',
        '• Não tenha medo de experimentar'
    ];

    const startY = (pageHeight - (instructions.length * instrConfig.text.lineHeight)) / 2;
    instructions.forEach((instruction, index) => {
        pdf.text(instruction, pageWidth * instrConfig.text.marginX, startY + (index * instrConfig.text.lineHeight));
    });

    await addLogo(pdf, pageWidth, pageHeight, paperSize, orientation);
};

// Página de imagem
const createImagePage = async (pdf, pageWidth, pageHeight, image, index, pageLayout, paperSize, orientation) => {
    const config = getLayoutConfig(paperSize, orientation);
    const imageConfig = config.image;

    pdf.addPage();
    addBorder(pdf, pageWidth, pageHeight);

    // Título da imagem
    pdf.setFontSize(imageConfig.title.fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(40, 40, 40);

    let imageTitle = image.title || `Desenho ${index + 1}`;
    if (imageTitle.length > 50) {
        imageTitle = imageTitle.substring(0, 47) + '...';
    }

    pdf.text(imageTitle, pageWidth / 2, imageConfig.title.y, { align: 'center' });

    // Área da imagem com margens seguras
    const maxWidth = pageWidth - (imageConfig.marginSides * 2);
    const maxHeight = pageHeight - imageConfig.marginTop - imageConfig.marginBottom;

    if (image.base64) {
        try {
            const imgData = new Image();
            imgData.src = image.base64;

            await new Promise((resolve) => {
                imgData.onload = resolve;
            });

            const imgRatio = imgData.width / imgData.height;
            let printWidth = maxWidth;
            let printHeight = printWidth / imgRatio;

            if (printHeight > maxHeight) {
                printHeight = maxHeight;
                printWidth = printHeight * imgRatio;
            }

            const startX = (pageWidth - printWidth) / 2;
            const startY = imageConfig.marginTop + ((maxHeight - printHeight) / 2);

            pdf.addImage(image.base64, 'PNG', startX, startY, printWidth, printHeight);

        } catch (error) {
            console.error('Erro ao adicionar imagem ao PDF:', error);
            addImagePlaceholder(pdf, imageConfig.marginSides, imageConfig.marginTop, maxWidth, maxHeight);
        }
    } else {
        addImagePlaceholder(pdf, imageConfig.marginSides, imageConfig.marginTop, maxWidth, maxHeight);
    }

    // Número da página
    pdf.setFontSize(imageConfig.pageNumber.fontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Página ${index + 1}`, pageWidth / 2, imageConfig.pageNumber.y, { align: 'center' });

    await addLogo(pdf, pageWidth, pageHeight, paperSize, orientation);
};

// Função auxiliar para placeholder
const addImagePlaceholder = (pdf, x, y, width, height) => {
    pdf.setFillColor(250, 250, 250);
    pdf.rect(x, y, width, height, 'F');

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(150, 150, 150);

    const centerX = x + (width / 2);
    const centerY = y + (height / 2);
    pdf.text('Imagem não disponível', centerX, centerY, { align: 'center' });
};

// Página de certificado
const createCertificatePage = async (pdf, pageWidth, pageHeight, paperSize, orientation) => {
    const config = getLayoutConfig(paperSize, orientation);
    const certConfig = config.certificate;

    pdf.addPage();
    addBorder(pdf, pageWidth, pageHeight);

    // Título
    pdf.setFontSize(certConfig.title.fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(40, 40, 40);
    pdf.text('Certificado de Conclusão', pageWidth / 2, certConfig.title.y, { align: 'center' });

    // Linha decorativa
    pdf.setDrawColor(150, 150, 150);
    pdf.setLineWidth(1);
    pdf.line(pageWidth * 0.2, certConfig.lineY, pageWidth * 0.8, certConfig.lineY);

    // Conteúdo
    const contentStartY = pageHeight * certConfig.contentStartY;

    pdf.setFontSize(certConfig.text.fontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Este livro foi colorido por:', pageWidth / 2, contentStartY, { align: 'center' });

    // Campo para nome
    const nameFieldY = contentStartY + certConfig.nameField.y;
    pdf.setLineWidth(0.5);
    const nameLineStart = pageWidth * ((1 - certConfig.nameField.width) / 2);
    const nameLineEnd = pageWidth * ((1 + certConfig.nameField.width) / 2);
    pdf.line(nameLineStart, nameFieldY, nameLineEnd, nameFieldY);

    pdf.setFontSize(12);
    pdf.text('Nome', pageWidth / 2, nameFieldY + 10, { align: 'center' });

    // Campo para data
    const dateFieldY = nameFieldY + certConfig.dateField.y;
    pdf.setFontSize(certConfig.text.fontSize);
    pdf.text('Data de conclusão:', pageWidth / 2, dateFieldY, { align: 'center' });

    const dateLineStart = pageWidth * ((1 - certConfig.dateField.width) / 2);
    const dateLineEnd = pageWidth * ((1 + certConfig.dateField.width) / 2);
    pdf.line(dateLineStart, dateFieldY + 20, dateLineEnd, dateFieldY + 20);

    pdf.setFontSize(12);
    pdf.text('____/____/________', pageWidth / 2, dateFieldY + 30, { align: 'center' });

    await addLogo(pdf, pageWidth, pageHeight, paperSize, orientation);
};

// Contracapa
const createBackCoverPage = async (pdf, pageWidth, pageHeight, paperSize, orientation) => {
    pdf.addPage();
    addBorder(pdf, pageWidth, pageHeight);

    await addLogo(pdf, pageWidth, pageHeight, paperSize, orientation);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(100, 100, 100);

    const messages = [
        'Parabéns por completar seu livro de colorir!',
        'Criado com Draw Your Goods',
        'https://drawgoods.com/'
    ];

    messages.forEach((message, index) => {
        pdf.text(message, pageWidth / 2, pageHeight / 2 + (index * 10) - 10, { align: 'center' });
    });
};